import React, { useEffect, useState } from "react";
import { Row, Col, FormGroup } from "reactstrap";

import { useFormik } from "formik";
import * as Yup from "yup";
import { useDispatch, useSelector } from "react-redux";

import { LabelText } from "../Utils/Input";
import InputField from "../Utils/InputField";
import SelectField from "../Utils/SelectField";
import CommonButton from "../Utils/CommonButton";
import { Loader } from "../Utils/Loader";

import { getItems } from "../../slices/Item/GetItem/thunk";
import { createAdjustment } from "../../slices/Adjustment/CreateAdjustment/thunk";

function AdjustmentForm({ closeModal, selectedRow, onSuccess }) {
  const dispatch = useDispatch();
  const { items } = useSelector((state) => state.GetItems || {});
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    dispatch(getItems(10000, 1));
  }, [dispatch]);

  const itemOptions =
    (items?.items || []).map((item) => ({
      label: item.itemname,
      value: item.itemid,
      sku: item.sku,
      itemname: item.itemname,
      category: item.categorypath?.[0] || "",
      locations: item.location || [],
      itemcount: item.itemcount || 0,
    })) || [];

  const formik = useFormik({
    initialValues: {
      itemid: null,
      category: "",
      fromlocation: null,
      quantity: "",
      netquantity: "",
      locationqty: "",
    },

    validationSchema: Yup.object({
      itemid: Yup.object().required("Required"),
      fromlocation: Yup.object().required("Required"),
      quantity: Yup.number()
        .required("Required")
        .test("not-zero", "Quantity cannot be 0", (value) => value !== 0),
    }),

    onSubmit: (values) => {
      const payload = {
        itemid: values.itemid?.value,
        quantity: Number(values.quantity),
        transactiontype: "adjustment",
        fromlocation: values.fromlocation?.value,
      };

      dispatch(
        createAdjustment(undefined, payload, setLoading, () => {
          onSuccess?.();
          closeModal?.();
        }),
      );
    },
  });

  useEffect(() => {
    if (!selectedRow || !items?.items) return;

    const item = items.items.find((i) => i.itemid === selectedRow.itemid);

    if (!item) return;

    setFieldValue("itemid", {
      label: item.itemname,
      value: item.itemid,
      sku: item.sku,
      itemname: item.itemname,
      category: item.categorypath?.[0] || "",
      locations: item.location || [],
      itemcount: item.itemcount || 0,
    });

    setFieldValue("category", item.categorypath?.[0] || "");
    setFieldValue("netquantity", item.itemcount || 0);
  }, [selectedRow, items]);

  const { values, errors, touched, setFieldValue, handleSubmit } = formik;

  const fromLocationOptions = values.itemid?.locations?.length
    ? values.itemid.locations.map((loc) => ({
        label: loc.locationname,
        value: loc.locationid,
        locationqty: loc.locationquantity,
      }))
    : [];

  return (
    <>
      {loading && <Loader />}
      <form onSubmit={handleSubmit}>
        <Row className="gy-3">
          {/* ITEM */}
          <Col md={6}>
            <FormGroup>
              <LabelText
                displayText={"Product"}
                className={"fw-semibold text-dark mb-2"}
                important={"*"}
                className={"text-danger"}
              />

              <SelectField
                value={values.itemid}
                placeholder="Select Product"
                options={itemOptions}
                handleChange={(val) => {
                  setFieldValue("itemid", val);
                  setFieldValue("category", val?.category || "");
                  setFieldValue("netquantity", val?.itemcount || 0);
                  setFieldValue("fromlocation", null);
                  setFieldValue("quantity", "");
                  setFieldValue("locationqty", 0);
                }}
                className={touched.itemid && errors.itemid ? "error-input" : ""}
                isDisabled={selectedRow?.disabledItem}
              />
            </FormGroup>
          </Col>

          {/* CATEGORY */}
          <Col md={6}>
            <FormGroup>
              <LabelText
                displayText={"Category"}
                className={"fw-semibold mb-2"}
              />

              <InputField
                name="category"
                type="text"
                value={values.category}
                disabled={true}
                className="bg-light"
              />
            </FormGroup>
          </Col>

          {/* FROM LOCATION */}
          <Col md={6}>
            <FormGroup>
              <LabelText
                displayText={"Location"}
                className={"fw-semibold mb-2"}
                important={"*"}
                className={"text-danger"}
              />

              <SelectField
                value={values.fromlocation}
                placeholder="Select Location"
                options={fromLocationOptions}
                handleChange={(val) => {
                  setFieldValue("fromlocation", val);
                  setFieldValue("locationqty", val?.locationqty || 0);
                }}
                className={
                  touched.fromlocation && errors.fromlocation
                    ? "error-input"
                    : ""
                }
              />
            </FormGroup>
          </Col>

          <Col md={6}>
            <FormGroup>
              <LabelText
                displayText={"Location Quantity"}
                className={"fw-semibold text-dark mb-2"}
              />

              <InputField
                name="locationqty"
                type="number"
                value={values.locationqty}
                disabled
                className="bg-light"
              />
            </FormGroup>
          </Col>

          {/* NET QUANTITY */}
          <Col md={6}>
            <FormGroup>
              <LabelText
                displayText={"Available Quantity"}
                className={"fw-semibold mb-2"}
              />

              <InputField
                name="netquantity"
                type="number"
                value={values.netquantity}
                disabled={true}
                className="bg-light"
              />
            </FormGroup>
          </Col>

          {/* QUANTITY */}
          <Col md={6}>
            <FormGroup>
              <LabelText
                displayText={"Adjustment Quantity"}
                className={"fw-semibold mb-2"}
                important={"*"}
                className={"text-danger"}
              />

              <InputField
                name="quantity"
                type="number"
                value={values.quantity}
                handleChange={(e) =>
                  setFieldValue(
                    "quantity",
                    e.target.value === "" ? "" : Number(e.target.value),
                  )
                }
                invalid={touched.quantity && errors.quantity}
              />
            </FormGroup>
          </Col>

          <div
            className="d-flex justify-content-center w-100 gap-2 flex-wrap pe-1 py-3"
            style={{
              position: "sticky",
              bottom: 0,
              backgroundColor: "#fff",
              borderTop: "1px solid #e9ebec",
              marginTop: "10px",
            }}
          >
            <div className="d-flex gap-2 justify-content-center">
              <CommonButton
                outline={true}
                className="btn-label rv-secondary-btn rounded-1 px-1 ps-4 py-1 w-100 w-md-auto text-nowrap"
                color={"main-color"}
                type={"button"}
                buttonAction={() => closeModal()}
                buttonText={"Cancel"}
                icon={
                  <i className="ri-close-fill label-icon secondary-label-icon align-middle fs-16 me-2"></i>
                }
                btnTxtClsName="px-2 ps-3 ms-2 me-1 fw-bold"
              />

              <CommonButton
                color={"main-color"}
                className="btn-label rv-primary-btn rounded-1 px-1 ps-4 py-1 w-100 w-md-auto text-nowrap"
                icon={
                  <i className="ri-checkbox-circle-line label-icon align-middle fs-16 me-2"></i>
                }
                btnTxtClsName="px-2 ps-3 ms-2 me-1 fw-bold"
                type={"submit"}
                buttonText={"Submit"}
                disabled={loading}
              />
            </div>
          </div>
        </Row>
      </form>
    </>
  );
}

export default AdjustmentForm;
